/**
 * @owner @Appboy/frontend-ix
 */

import type { I18nConfig } from "@braze/i18n-scripts";
import { loadConfig } from "@braze/i18n-scripts";
import path from "path";
import { setupI18n } from "@braze/i18n-core";

import { BrazeICU } from "@lib/BrazeICU";

import type allTranslations from "./translations.braze.json";

declare const jest: any;

declare global {
  interface Window {
    /**
     * Translations for a single locale, keyed by locale, as assigned by the
     * locale's translations bundle (see script/i18n/split-translations.js).
     */
    brazeTranslations?: Partial<typeof allTranslations>;
  }
}

/**
 * The translations available at runtime.
 *
 * translations.braze.json holds every locale and is far too large to ship in the
 * dashboard bundle, so `yarn translations:generate` splits it into one bundle
 * per locale and the Rails layout loads the bundle for the locale the page is
 * rendered in, which assigns `window.brazeTranslations`. Surfaces that render
 * without that layout - Storybook, Cypress and Jest - register every locale
 * themselves through createI18nDev.
 *
 * The type covers every locale so translation keys stay statically checked, even
 * though only one locale (or none, outside the browser) is present at runtime.
 */
const translations = (
  typeof window === "undefined" ? {} : (window.brazeTranslations ?? {})
) as typeof allTranslations;

function getConfig() {
  const configArgument =
    // if window is not defined (command-line) or we're in a jest test, use the node version
    window === undefined || typeof jest === "object"
      ? path.resolve(__dirname, "..", "..", "..", ".i18nConfig.json")
      : // eslint-disable-next-line @typescript-eslint/no-var-requires
        (require("./.i18nConfig.json") as I18nConfig);
  return loadConfig(configArgument);
}
const config = getConfig();

// usage: https://confluence.braze.com/display/DASHINFRA/Internationalization+for+dashboard+developers
export const i18n = setupI18n<typeof translations>(
  translations,
  config,
  () => {
    BrazeICU.setActiveTimezone();
  },
  BrazeICU
);
